import { getMediaUrl, isDebug } from "@/constants";
import { customSetManifestFileName } from "@/constants/diceConfig";
import { getManifestData } from "@/helpers/getManifestData";
import { preloadAssets } from "@/helpers/preloadAssets";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useDiceSets } from "@/hooks/useDiceSets";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { Manifest } from "@/types";
import type { User } from "@dndbeyond/auth/types/user";
import { useEffect, useState } from "react";

export const useUserManifest = (
  nodeEnv?: string,
  env?: string,
  user?: User,
  preview?: boolean,
  canUseShared3dDice?: boolean,
) => {
  const [userManifest, setUserManifest] = useState<Record<string, Manifest>>();
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const selectedSet = useDicePreviewerStore((state) => state.selectedSet);
  const mediaUrl = getMediaUrl(String(nodeEnv), String(env));
  const diceSets = useDiceSets(nodeEnv, env);
  const userSetId = useUserDiceDataStore(
    (state) => state.getUserDiceData(Number(user?.id))?.setId,
  );

  useEffect(() => {
    if (!(Boolean(canUseShared3dDice) && nodeEnv && env && diceSets)) return;

    const setId = preview ? selectedSet?.id : userSetId;

    (async () => {
      const manifestUrl =
        customUrlMap?.[customSetManifestFileName] ||
        diceSets.find(({ id }) => id === setId)?.manifestUrl;

      if (!manifestUrl) {
        if (isDebug) {
          console.warn(
            `[DiceCanvases] Manifest URL for set ID "${setId}" not found.`,
          );
        }

        return;
      }

      const manifestData = await getManifestData({
        mediaUrl,
        setId: String(setId),
        manifestUrl,
        customUrlMap,
      });

      if (!manifestData) return;

      setUserManifest({
        [String(setId)]: manifestData,
      });
      preloadAssets(mediaUrl, [manifestData], customUrlMap);
    })();
  }, [
    canUseShared3dDice,
    customUrlMap,
    diceSets,
    env,
    mediaUrl,
    nodeEnv,
    preview,
    selectedSet?.id,
    userSetId,
  ]);

  return userManifest;
};
